import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { fetchMetrics } from '../../services/metricsService';
import type { MetricsRange, MetricsResponse } from '../../types/Metrics';
import { ROUTES } from '../../utils/constants';
import { SimpleLineChart } from './charts/SimpleLineChart';
import { adm } from './adminTheme';

const RANGES: { id: MetricsRange; label: string }[] = [
  { id: '1h', label: 'Son 1 saat' },
  { id: '24h', label: 'Son 24 saat' },
  { id: '7d', label: 'Son 7 gün' },
];

function buildLogsUrl(query: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== '') sp.set(k, v);
  }
  const s = sp.toString();
  return s ? `${ROUTES.ADMIN_LOGS}?${s}` : ROUTES.ADMIN_LOGS;
}

function formatPct(v: number | null, digits = 0): string {
  if (v == null) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(digits)}%`;
}

function MetricsSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-24 rounded-lg bg-white/[0.05]" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 rounded-lg bg-white/[0.05]" />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-36 rounded-lg bg-white/[0.05]" />
        ))}
      </div>
    </div>
  );
}

export const AppMetrics: React.FC = () => {
  const [range, setRange] = useState<MetricsRange>('24h');
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMetrics(range);
      setData(res.data);
    } catch {
      setError('Metrikler yüklenemedi.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    void load();
  }, [load]);

  const rangeIso = useMemo(() => {
    if (!data) return { from: '', to: '' };
    return { from: data.from, to: data.to };
  }, [data]);

  const hasErrorSeries = useMemo(() => {
    if (!data) return false;
    return data.summary.errorCount > 0 || data.errorsOverTime.some((x) => x.count > 0);
  }, [data]);

  const summaryLinks = useMemo(() => {
    const { from, to } = rangeIso;
    const base = { dateFrom: from, dateTo: to };
    return {
      all: buildLogsUrl({ ...base, pill: 'requests' }),
      errors: buildLogsUrl({ ...base, pill: '5xx' }),
      slow: buildLogsUrl({ ...base, pill: 'slow', slowMs: '1000' }),
      latency: buildLogsUrl({ ...base, pill: 'slow', slowMs: '1000' }),
    };
  }, [rangeIso]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl space-y-1">
          <h2 className="text-lg font-semibold tracking-tight text-[#eceef3] sm:text-xl">App Metrics</h2>
          <p className="text-sm text-[#8b92a1]">
            {data
              ? `${format(new Date(data.from), 'PPp', { locale: tr })} – ${format(new Date(data.to), 'PPp', { locale: tr })} · önceki dönemle kıyas`
              : 'Yükleniyor…'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg border border-white/[0.08] bg-[#080a0f] p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setRange(r.id)}
                className={`rounded-md px-3 py-2 text-xs font-medium ${
                  range === r.id ? 'bg-white/[0.08] text-[#eceef3]' : 'text-[#6b7280] hover:text-[#a8b0bf]'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/25 bg-red-950/20 px-4 py-3 text-sm text-red-300">{error}</div>
      )}

      {loading && !data ? (
        <MetricsSkeleton />
      ) : data ? (
        <>
          <section className="space-y-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7d8696]">
              Tespit edilen sorunlar
            </h3>
            {data.issues.length === 0 ? (
              <div className="rounded-lg border border-white/[0.06] bg-[#0a0c11] px-4 py-3 text-sm text-[#8b92a1]">
                Otomatik eşik kontrolünde uyarı yok. Özet ve tablolara bakın.
              </div>
            ) : (
              <ul className="space-y-2">
                {data.issues.map((issue, i) => (
                  <li key={i}>
                    <Link
                      to={buildLogsUrl(issue.logsQuery)}
                      className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm transition hover:bg-white/[0.03] ${
                        issue.severity === 'high'
                          ? 'border-red-500/25 bg-red-950/15 text-[#fecaca]'
                          : 'border-amber-500/20 bg-amber-950/10 text-[#fde68a]'
                      }`}
                    >
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 opacity-80" />
                      <span className="min-w-0 flex-1 leading-snug">{issue.text}</span>
                      <span className="shrink-0 text-[11px] font-medium text-[#93a4ff]">Loglar →</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7d8696]">Özet</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <Link
                to={summaryLinks.all}
                className={`${adm.statCard} ${adm.cardClickable}`}
              >
                <p className={adm.statLabel}>İstekler</p>
                <p className={adm.statValue}>{data.summary.totalRequests}</p>
                <p className={`${adm.statSub} mt-1 flex items-center gap-1`}>
                  {data.compare.delta.totalRequestsPct != null ? (
                    <>
                      {data.compare.delta.totalRequestsPct > 0 ? (
                        <ArrowUpRight className="h-3.5 w-3.5 text-amber-400" />
                      ) : data.compare.delta.totalRequestsPct < 0 ? (
                        <ArrowDownRight className="h-3.5 w-3.5 text-emerald-400" />
                      ) : (
                        <Minus className="h-3.5 w-3.5 text-[#6b7280]" />
                      )}
                      <span className="text-[#9ca3b8]">önceki dönem: {formatPct(data.compare.delta.totalRequestsPct)}</span>
                    </>
                  ) : (
                    <span className="text-[#6b7280]">önceki dönemle kıyas yok</span>
                  )}
                </p>
              </Link>

              <Link to={summaryLinks.errors} className={`${adm.statCard} ${adm.cardClickable}`}>
                <p className={adm.statLabel}>Hata oranı (5xx)</p>
                <p className={adm.statValue}>{(data.summary.errorRate * 100).toFixed(2)}%</p>
                <p className={`${adm.statSub} mt-1`}>
                  {data.summary.errorCount} adet 5xx ·{' '}
                  <span
                    className={
                      data.compare.delta.errorRatePctPoints > 0
                        ? 'text-red-400'
                        : data.compare.delta.errorRatePctPoints < 0
                          ? 'text-emerald-400'
                          : 'text-[#6b7280]'
                    }
                  >
                    {data.compare.delta.errorRatePctPoints === 0
                      ? 'pp değişim yok'
                      : data.compare.delta.errorRatePctPoints > 0
                        ? `↑ ${(data.compare.delta.errorRatePctPoints * 100).toFixed(2)} pp (kötü)`
                        : `↓ ${Math.abs(data.compare.delta.errorRatePctPoints * 100).toFixed(2)} pp (iyi)`}
                  </span>
                </p>
              </Link>

              <Link to={summaryLinks.latency} className={`${adm.statCard} ${adm.cardClickable}`}>
                <p className={adm.statLabel}>Ort. süre</p>
                <p className={adm.statValue}>{data.summary.avgDurationMs} ms</p>
                <p className={`${adm.statSub} mt-1 flex items-center gap-1`}>
                  {data.compare.delta.avgDurationMsPct != null ? (
                    <>
                      {data.compare.delta.avgDurationMsPct > 0 ? (
                        <ArrowUpRight className="h-3.5 w-3.5 text-amber-400" />
                      ) : data.compare.delta.avgDurationMsPct < 0 ? (
                        <ArrowDownRight className="h-3.5 w-3.5 text-emerald-400" />
                      ) : (
                        <Minus className="h-3.5 w-3.5 text-[#6b7280]" />
                      )}
                      <span className="text-[#9ca3b8]">{formatPct(data.compare.delta.avgDurationMsPct)} vs önceki</span>
                    </>
                  ) : (
                    <span className="text-[#6b7280]">—</span>
                  )}
                </p>
              </Link>

              <Link to={summaryLinks.slow} className={`${adm.statCard} ${adm.cardClickable}`}>
                <p className={adm.statLabel}>Yavaş istek</p>
                <p className={adm.statValue}>{data.summary.slowRequestCount}</p>
                <p className={`${adm.statSub} mt-1`}>
                  ≥ 1000 ms ·{' '}
                  {data.compare.delta.slowRequestCountPct != null ? (
                    <span className="text-[#9ca3b8]">{formatPct(data.compare.delta.slowRequestCountPct)} vs önceki</span>
                  ) : (
                    '—'
                  )}
                </p>
              </Link>
            </div>
          </section>

          <section>
            <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7d8696]">Top problems</h3>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div className={adm.panel}>
                <div className={adm.panelHeader}>
                  <h4 className={adm.panelTitle}>Top errors (mesaj)</h4>
                </div>
                <div className={adm.tableWrap}>
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-white/[0.06]">
                        <th className={`${adm.tableHead} px-4 py-2`}>Mesaj</th>
                        <th className={`${adm.tableHead} px-4 py-2 text-right`}>Adet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topErrorMessages.length === 0 ? (
                        <tr>
                          <td colSpan={2} className="px-4 py-8 text-center text-[#6b7280]">
                            5xx mesajı yok
                          </td>
                        </tr>
                      ) : (
                        data.topErrorMessages.map((row, idx) => (
                          <tr key={idx} className={adm.tableRow}>
                            <td className={`${adm.tableCell} max-w-[1px]`}>
                              <Link
                                to={buildLogsUrl({
                                  pill: '5xx',
                                  q: row.message.slice(0, 200),
                                  dateFrom: rangeIso.from,
                                  dateTo: rangeIso.to,
                                })}
                                className={adm.link}
                              >
                                <span className="line-clamp-2 break-words">{row.message}</span>
                              </Link>
                            </td>
                            <td className={`${adm.tableCell} text-right tabular-nums`}>{row.count}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={adm.panel}>
                <div className={adm.panelHeader}>
                  <h4 className={adm.panelTitle}>En çok 5xx (route)</h4>
                </div>
                <div className={adm.tableWrap}>
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-white/[0.06]">
                        <th className={`${adm.tableHead} px-4 py-2`}>Route</th>
                        <th className={`${adm.tableHead} px-4 py-2 text-right`}>5xx</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topFailedEndpoints.length === 0 ? (
                        <tr>
                          <td colSpan={2} className="px-4 py-8 text-center text-[#6b7280]">
                            Veri yok
                          </td>
                        </tr>
                      ) : (
                        data.topFailedEndpoints.map((e) => (
                          <tr key={e.route} className={adm.tableRow}>
                            <td className={`${adm.tableCell} font-mono text-[12px]`}>
                              <Link
                                to={buildLogsUrl({
                                  route: e.route,
                                  pill: '5xx',
                                  dateFrom: rangeIso.from,
                                  dateTo: rangeIso.to,
                                })}
                                className={adm.link}
                              >
                                {e.route}
                              </Link>
                            </td>
                            <td className={`${adm.tableCell} text-right tabular-nums`}>{e.errors}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={adm.panel}>
                <div className={adm.panelHeader}>
                  <h4 className={adm.panelTitle}>En yavaş (ort. ms)</h4>
                </div>
                <div className={adm.tableWrap}>
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-white/[0.06]">
                        <th className={`${adm.tableHead} px-4 py-2`}>Route</th>
                        <th className={`${adm.tableHead} px-4 py-2 text-right`}>Ort.</th>
                        <th className={`${adm.tableHead} px-4 py-2 text-right`}>n</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.slowestEndpoints.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="px-4 py-8 text-center text-[#6b7280]">
                            Veri yok
                          </td>
                        </tr>
                      ) : (
                        data.slowestEndpoints.map((e) => (
                          <tr key={e.route} className={adm.tableRow}>
                            <td className={`${adm.tableCell} font-mono text-[12px]`}>
                              <Link
                                to={buildLogsUrl({
                                  route: e.route,
                                  pill: 'slow',
                                  slowMs: '1000',
                                  dateFrom: rangeIso.from,
                                  dateTo: rangeIso.to,
                                })}
                                className={adm.link}
                              >
                                {e.route}
                              </Link>
                            </td>
                            <td className={`${adm.tableCell} text-right tabular-nums`}>{e.avgMs}</td>
                            <td className={`${adm.tableCell} text-right tabular-nums text-[#8b92a1]`}>{e.count}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7d8696]">Zaman serisi</h3>
            <div className="grid grid-cols-1 gap-6">
              <div className={adm.panel}>
                <div className="border-b border-white/[0.06] px-4 py-2.5">
                  <span className="text-xs font-medium text-[#c8cdd8]">İstekler</span>
                </div>
                <div className="p-4 sm:p-5">
                  <SimpleLineChart
                    values={data.requestsOverTime.map((x) => x.count)}
                    labels={data.requestsOverTime.map((x) => x.t)}
                    stroke="#73BF69"
                    height={340}
                    seriesName="İstekler"
                    interpolation="step"
                    formatValue={(v) => `${Math.round(v).toLocaleString('tr-TR')} istek`}
                  />
                </div>
              </div>
              {hasErrorSeries ? (
                <div className={adm.panel}>
                  <div className="border-b border-white/[0.06] px-4 py-2.5">
                    <span className="text-xs font-medium text-[#c8cdd8]">5xx (zaman)</span>
                  </div>
                  <div className="p-4 sm:p-5">
                    <SimpleLineChart
                      values={data.errorsOverTime.map((x) => x.count)}
                      labels={data.errorsOverTime.map((x) => x.t)}
                      stroke="#F2495C"
                      height={340}
                      seriesName="5xx hata"
                      interpolation="step"
                      formatValue={(v) => `${Math.round(v).toLocaleString('tr-TR')} adet`}
                    />
                  </div>
                </div>
              ) : (
                <div className={`${adm.panel} flex flex-col justify-center px-4 py-10`}>
                  <p className="text-xs font-medium text-[#c8cdd8]">5xx (zaman)</p>
                  <p className="mt-2 text-sm text-[#6b7280]">Bu aralıkta 5xx yok; grafik gizlendi.</p>
                  <Link to={summaryLinks.errors} className={`${adm.link} mt-2 inline text-xs`}>
                    5xx loglarına git →
                  </Link>
                </div>
              )}
              <div className={adm.panel}>
                <div className="border-b border-white/[0.06] px-4 py-2.5">
                  <span className="text-xs font-medium text-[#c8cdd8]">Ortalama gecikme (ms)</span>
                </div>
                <div className="p-4 sm:p-5">
                  <SimpleLineChart
                    values={data.latencyOverTime.map((x) => x.avgMs)}
                    labels={data.latencyOverTime.map((x) => x.t)}
                    stroke="#5794F2"
                    height={340}
                    seriesName="Ortalama gecikme"
                    formatValue={(v) => `${Math.round(v).toLocaleString('tr-TR')} ms`}
                  />
                </div>
              </div>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
};
